import { Mark, mergeAttributes, type Editor } from '@tiptap/core'
import { Plugin, PluginKey, TextSelection, type Transaction } from '@tiptap/pm/state'
import { Fragment, Slice, type Mark as PmMark, type Node as PmNode } from '@tiptap/pm/model'
import { anchorClose, matchAnchor, readAnchor, type Anchor } from '../../lib/anchors'

/**
 * The prose anchor (AB#350): a named target on a text range, that a link of the same artefact
 * points at with `[text](#anchor-<id>)`.
 *
 * WHY A MARKDOWN SYNTAX. A part IS its markdown (tiptap-markdown runs with `html: false`), so the
 * anchor is written as a bracketed span in the text colour's family (text-colour-mark.ts):
 *
 *   [words]{anchor=<id> name="Pricing summary"}
 *
 * lib/anchors.ts owns the form (anchorClose / matchAnchor). The opener is the fixed `[`, so
 * tiptap-markdown's whitespace re-flow of an opening delimiter cannot break it.
 *
 * PARITY. `backend/adapters/markdown/anchor-markdown.ts` is the same extension for the server
 * canonicaliser (same name, priority, attributes and markdown form) — both must produce the same
 * bytes, which the `anchor-*` cases of `shared/md-canonical.fixture.json` prove. `inclusive` and the
 * paste plugin below only steer EDITING (typing at the edge, pasting); they never reach the bytes.
 */

/** The DOM attributes the markdown-it rule hands the anchor over in. */
const ID_ATTR = 'data-anchor'
const NAME_ATTR = 'data-anchor-name'

/** Minimal structural view of markdown-it — just what the inline rule touches. */
interface MdToken {
  attrs: Array<[string, string]> | null
  markup: string
}
interface MdInlineState {
  src: string
  pos: number
  posMax: number
  md: { inline: { tokenize(state: MdInlineState): void } }
  push(type: string, tag: string, nesting: number): MdToken
}
interface MdIt {
  inline: { ruler: { before(before: string, name: string, rule: (state: MdInlineState, silent: boolean) => boolean): void } }
}

/** tiptap-markdown runs every extension's `parse.setup` before EVERY parse, on one long-lived
 *  markdown-it instance — register the rule once per instance. */
const withRule = new WeakSet<object>()

/** The markdown-it inline rule: `[content]{anchor=<id> name="…"}` (lib/anchors.ts#matchAnchor). */
export function anchorRule(state: MdInlineState, silent: boolean): boolean {
  const max = state.posMax
  const m = matchAnchor(state.src, state.pos, max)
  if (!m) return false
  if (!silent) {
    const open = state.push('niobe_anchor_open', 'span', 1)
    open.attrs = [
      [ID_ATTR, m.anchor.id],
      [NAME_ATTR, m.anchor.name],
    ]
    open.markup = '['
    state.pos = m.from
    state.posMax = m.close
    state.md.inline.tokenize(state)
    state.posMax = max
    state.push('niobe_anchor_close', 'span', -1).markup = ']'
  }
  state.pos = m.end
  return true
}

// ── reading and changing the anchors of a document ───────────────────────────────────────────────

/** The anchor carried by a mark, or none. */
const anchorOfMark = (m: PmMark): Anchor | undefined => (m.type.name === 'anchor' ? readAnchor(m.attrs) : undefined)

/** One text range carrying an anchor mark. */
export interface AnchorRange {
  from: number
  to: number
  mark: PmMark
}

/** Every text range of `doc` carrying the anchor `id`, in document order. */
export function anchorRanges(doc: PmNode, id: string): AnchorRange[] {
  const out: AnchorRange[] = []
  doc.descendants((node, pos) => {
    if (!node.isText) return
    const mark = node.marks.find((m) => anchorOfMark(m)?.id === id)
    if (mark) out.push({ from: pos, to: pos + node.nodeSize, mark })
  })
  return out
}

/** The ids of every anchor in `doc`. */
export function anchorIdsIn(doc: PmNode): Set<string> {
  const ids = new Set<string>()
  doc.descendants((node) => {
    if (!node.isText) return
    for (const m of node.marks) {
      const a = anchorOfMark(m)
      if (a) ids.add(a.id)
    }
  })
  return ids
}

/** The anchor at the selection (the caret inside it, or a selection starting in it), or null. */
export function anchorAtSelection(editor: Editor): Anchor | null {
  if (!editor.isActive('anchor')) return null
  return readAnchor(editor.getAttributes('anchor')) ?? null
}

/** Rename the anchor `id` on every range carrying it (one transaction). False: none found. */
export function renameAnchorIn(tr: Transaction, id: string, name: string): boolean {
  const ranges = anchorRanges(tr.doc, id)
  if (ranges.length === 0) return false
  const type = ranges[0]!.mark.type
  // An anchor mark excludes another of its type, so adding the renamed one replaces it.
  for (const r of ranges) tr.addMark(r.from, r.to, type.create({ id, name }))
  return true
}

/** Remove the anchor `id` everywhere, its text kept (one transaction). False: none found. */
export function removeAnchorIn(tr: Transaction, id: string): boolean {
  const ranges = anchorRanges(tr.doc, id)
  for (const r of ranges) tr.removeMark(r.from, r.to, r.mark)
  return ranges.length > 0
}

/**
 * Select the anchor `id` (its first stretch of contiguous ranges) and scroll it into view. False when
 * the document holds no such anchor.
 */
export function revealAnchor(editor: Editor, id: string): boolean {
  if (editor.isDestroyed) return false
  const ranges = anchorRanges(editor.state.doc, id)
  const first = ranges[0]
  if (!first) return false
  let to = first.to
  for (const r of ranges.slice(1)) {
    if (r.from !== to) break
    to = r.to
  }
  const { state, view } = editor
  view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, first.from, to)).scrollIntoView())
  if (view.editable) view.focus()
  return true
}

/** `slice` with every anchor mark whose id is in `taken` removed — its text kept. */
export function stripAnchors(slice: Slice, taken: ReadonlySet<string>): Slice {
  if (taken.size === 0) return slice
  const strip = (fragment: Fragment): Fragment => {
    const nodes: PmNode[] = []
    fragment.forEach((node) => {
      if (node.isText) {
        const marks = node.marks.filter((m) => {
          const a = anchorOfMark(m)
          return !a || !taken.has(a.id)
        })
        nodes.push(marks.length === node.marks.length ? node : node.mark(marks))
      } else nodes.push(node.copy(strip(node.content)))
    })
    return Fragment.fromArray(nodes)
  }
  return new Slice(strip(slice.content), slice.openStart, slice.openEnd)
}

export const ProseAnchor = Mark.create({
  name: 'anchor',
  // Ranked OUTSIDE every other mark, the text colour (1100) and the link (1000) included: an
  // anchored coloured link is `[[[label](url)]{color=red}]{anchor=…}`, one span around them all.
  priority: 1200,
  // Typing at either edge of an anchored range does not grow the anchor: the target stays the
  // words the member anchored. (Editing only — the markdown bytes never depend on it.)
  inclusive() {
    return false
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute(ID_ATTR),
        renderHTML: (attrs: { id?: unknown }) => (typeof attrs.id === 'string' ? { [ID_ATTR]: attrs.id } : {}),
      },
      name: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute(NAME_ATTR),
        renderHTML: (attrs: { name?: unknown }) => (typeof attrs.name === 'string' ? { [NAME_ATTR]: attrs.name } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: `span[${ID_ATTR}]` }]
  },

  renderHTML({ HTMLAttributes }) {
    // `prose-anchor`: the subtle on-screen cue (styles.css) — the editor's DOM only, never exported.
    return ['span', mergeAttributes(HTMLAttributes, { class: 'prose-anchor' }), 0]
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('proseAnchorPaste'),
        props: {
          // Copy/paste never duplicates an anchor id: an anchor still in the document is stripped
          // from what is pasted (its text kept). A cut removed the original first, so a cut + paste
          // keeps it; a drag that MOVES text keeps it too (the original goes when it lands).
          transformPasted: (slice, view) => {
            if ((view as unknown as { dragging?: { move?: boolean } | null }).dragging?.move) return slice
            return stripAnchors(slice, anchorIdsIn(view.state.doc))
          },
        },
      }),
    ]
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '[',
          close: (_state: unknown, mark: { attrs: { id: string; name: string } }) => anchorClose({ id: mark.attrs.id, name: mark.attrs.name }),
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {
          setup(markdownit: MdIt) {
            if (withRule.has(markdownit)) return
            withRule.add(markdownit)
            // Before `link`: `[…]{anchor=…}` is never a link, and a link never ends in `]{anchor=`.
            markdownit.inline.ruler.before('link', 'niobe_anchor', anchorRule)
          },
        },
      },
    }
  },
})
